import { useEffect, useState } from "react";
import type { TranslateFn } from "../../i18n";
import type { FaceLandmarks } from "../../face-detect";
import type { BodyLandmarks } from "../../pose-detect";
import { EngagingTryOnView } from "./EngagingTryOnView";
import { PoseOverlay } from "./PoseOverlay";

/**
 * Mobile-only scanning / processing view shown after the user taps
 * ANALYZE MY SIZE on the photo step. Matches the reference design:
 *
 *   ┌─ X close (top-left) | SIZE CONSULTANT (header)
 *   │
 *   │ ┌─────────────────────────────────┐
 *   │ │ ●LIVE_FEED:04                   │
 *   │ │     ┌─ ─ ─ ─ ─ ─ ─ ┐            │
 *   │ │     │ user photo  │ ← MediaPipe │
 *   │ │     │ + skeleton  │   skeleton  │
 *   │ │     └─ ─ ─ ─ ─ ─ ─ ┘            │
 *   │ │     DETECTING POSE...           │
 *   │ │     ▰▰▰▰▰▰▰▰▰▱▱▱                │
 *   │ │     PLEASE STAND STILL          │
 *   │ └─────────────────────────────────┘
 *   │
 *   │ SCANNING FRAME              ← cycling stage title
 *   │ Our AI is mapping your...   ← cycling stage description
 *   │
 *   └─ MANUAL FIT | AI SCAN
 *
 * The text below the image cycles through stages with a slide+fade
 * animation as the analysis progresses. MediaPipe pose detection
 * runs in the background (the parent passes bodyLandmarks once it
 * resolves) and the skeleton is overlaid the moment it arrives —
 * matching the desktop behaviour.
 */

// MobileSkeleton was inlined here previously. The same renderer is used
// by the in-wizard profile-creation calculating step, so it now lives in
// PoseOverlay.tsx as a shared component. Pass `width`/`height` instead
// of `w`/`h` and supply a className for the existing CSS positioning.
function MobileSkeleton({ landmarks, w, h }: { landmarks: BodyLandmarks; w: number; h: number }) {
  return <PoseOverlay landmarks={landmarks} width={w} height={h} minVisibility={0.12} className="ps-msc-pose-overlay" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

function MobileFaceOverlay({ landmarks, w, h }: { landmarks: FaceLandmarks; w: number; h: number }) {
  type Pt = { x: number; y: number };
  const W = w;
  const H = h;
  const pts: Array<{ key: string; p: Pt }> = [
    { key: "forehead", p: landmarks.forehead },
    { key: "chin", p: landmarks.chin },
    { key: "noseTip", p: landmarks.noseTip },
    { key: "noseBridge", p: landmarks.noseBridge },
    { key: "leftInnerEye", p: landmarks.leftInnerEye },
    { key: "rightInnerEye", p: landmarks.rightInnerEye },
    { key: "leftOuterEye", p: landmarks.leftOuterEye },
    { key: "rightOuterEye", p: landmarks.rightOuterEye },
    { key: "leftTragus", p: landmarks.leftTragus },
    { key: "rightTragus", p: landmarks.rightTragus },
    { key: "leftMouth", p: landmarks.leftMouth },
    { key: "rightMouth", p: landmarks.rightMouth },
  ];
  const connections: Array<[Pt, Pt]> = [
    [landmarks.forehead, landmarks.noseBridge],
    [landmarks.noseBridge, landmarks.noseTip],
    [landmarks.noseTip, landmarks.chin],
    [landmarks.leftOuterEye, landmarks.leftInnerEye],
    [landmarks.leftInnerEye, landmarks.rightInnerEye],
    [landmarks.rightInnerEye, landmarks.rightOuterEye],
    [landmarks.leftTragus, landmarks.leftOuterEye],
    [landmarks.rightOuterEye, landmarks.rightTragus],
    [landmarks.leftMouth, landmarks.rightMouth],
  ];
  const faceSpan = Math.max(1, Math.abs(landmarks.rightOuterEye.x - landmarks.leftOuterEye.x) * W);
  const strokeW = faceSpan * 0.022;
  const halo = faceSpan * 0.04;
  const core = halo * 0.55;

  return (
    <svg
      className="ps-msc-pose-overlay"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
    >
      {connections.map(([a, b], i) => (
        <line
          key={`mfl-${i}`}
          x1={a.x * W}
          y1={a.y * H}
          x2={b.x * W}
          y2={b.y * H}
          stroke="rgba(100,210,255,0.55)"
          strokeWidth={strokeW}
          strokeLinecap="round"
          opacity="0"
          style={{ animation: `ps-pose-fade 0.4s ease ${i * 0.05}s forwards` }}
        />
      ))}
      {[landmarks.leftIrisCenter, landmarks.rightIrisCenter].map((c, i) => {
        const ring = i === 0 ? landmarks.leftIrisRing : landmarks.rightIrisRing;
        const rx = ring?.length
          ? (Math.abs((ring[0]?.x ?? c.x) - (ring[2]?.x ?? c.x)) * W) / 2
          : faceSpan * 0.025;
        return (
          <circle
            key={`miris-${i}`}
            cx={c.x * W}
            cy={c.y * H}
            r={Math.max(faceSpan * 0.025, rx)}
            fill="none"
            stroke="rgba(255,230,120,0.95)"
            strokeWidth={strokeW * 0.75}
            opacity="0"
            style={{ animation: `ps-pose-fade 0.3s ease ${0.3 + i * 0.1}s forwards` }}
          />
        );
      })}
      {pts.map(({ key, p }, i) => (
        <g key={key}>
          <circle
            cx={p.x * W}
            cy={p.y * H}
            r={halo}
            fill="rgba(100,210,255,0.22)"
            opacity="0"
            style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }}
          />
          <circle
            cx={p.x * W}
            cy={p.y * H}
            r={core}
            fill="rgba(100,210,255,0.95)"
            opacity="0"
            style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }}
          />
        </g>
      ))}
    </svg>
  );
}

// ── Stage definitions: each entry is one frame of the cycling text ──
type Stage = { title: string; desc: string; viewfinderText: string };
type MeasurementType = "body" | "face" | "head" | "foot" | "wrist";

interface MobileScanningViewProps {
  /** User's photo URL for snap mode. Omit for text-mode sizing — the
   *  product image is shown instead and the MediaPipe skeleton overlay
   *  is suppressed. */
  previewUrl?: string;
  /** Falls back to this image when previewUrl is not provided */
  productImage?: string;
  bodyLandmarks: BodyLandmarks | null | undefined;
  faceLandmarks?: FaceLandmarks | null | undefined;
  measurementType?: MeasurementType;
  sizingDone: boolean;
  /** True while VTO is running — swaps to the engaging try-on UI
   *  (progress ring + WHILE YOU WAIT cards) instead of the cycling
   *  sizing stages. */
  tryOnProcessing?: boolean;
  /** Garment material — drives the "Garment Spotlight" card during try-on. */
  productMaterial?: string;
  /** Garment description — fallback for "Garment Spotlight" when material is empty. */
  productDescription?: string;
  /** Optional cancel handler shown as a text link under the cards. */
  onCancelTryOn?: () => void;
  /** No longer rendered (tabs removed mid-scan), kept for backwards-compat
   *  with the photo-step caller that passes it down. */
  onSwitchToManual?: () => void;
  t: TranslateFn;
}

export function MobileScanningView({
  previewUrl,
  productImage,
  bodyLandmarks,
  faceLandmarks,
  measurementType = "body",
  sizingDone,
  tryOnProcessing,
  productMaterial,
  productDescription,
  onCancelTryOn,
  // onSwitchToManual is intentionally not destructured — bottom tabs are
  // hidden during analysis and the prop is kept on the type for callers.
  t,
}: MobileScanningViewProps) {
  // Try-on now fires in parallel with sizing, so tryOnProcessing alone is
  // no longer a reason to swap to the engaging panel here — this component
  // only renders during the sizing-loading phase (isSnapProcessing is now
  // sizingLoading-only). The cycling-stages scanning UI below is the right
  // visual for sizing; the parallel try-on result will swap into the cards
  // view post-sizing.

  // Text-mode sizing has no user photo — show the product image
  // inside the viewfinder and suppress the MediaPipe skeleton.
  const displayImage = previewUrl || productImage || "";
  const isPhotoMode = !!previewUrl;
  const isFaceCategory = measurementType === "face" || measurementType === "head";
  const showFaceOverlay = isPhotoMode && isFaceCategory && !!faceLandmarks;
  const showBodyOverlay = isPhotoMode && !!bodyLandmarks && (!isFaceCategory || !faceLandmarks);
  const landmarksReady = isFaceCategory ? !!faceLandmarks || !!bodyLandmarks : !!bodyLandmarks;
  const stages: Stage[] = isPhotoMode
    ? isFaceCategory
      ? measurementType === "head"
        ? [
            { title: t("DETECTING HEAD"),        desc: t("Reading head landmarks from your photo."),                 viewfinderText: t("DETECTING HEAD") },
            { title: t("MAPPING CIRCUMFERENCE"), desc: t("Estimating head width and depth."),                       viewfinderText: t("MAPPING") },
            { title: t("MATCHING HAT SIZE"),     desc: t("Comparing your circumference to the size chart."),        viewfinderText: t("MATCHING SIZE") },
            { title: t("FINALIZING RESULT"),     desc: t("Almost done — preparing your suggested size."),           viewfinderText: t("FINALIZING") },
          ]
        : [
            { title: t("DETECTING FACE"),        desc: t("Identifying face landmarks in your photo."),              viewfinderText: t("DETECTING FACE") },
            { title: t("CALIBRATING SCALE"),     desc: t("Using iris size as the pixel-to-mm anchor."),             viewfinderText: t("CALIBRATING") },
            { title: t("MEASURING FRAME"),       desc: t("Mapping bridge, frame width and temple fit."),            viewfinderText: t("MEASURING") },
            { title: t("MATCHING FRAME SIZE"),   desc: t("Comparing your measurements to the size chart."),         viewfinderText: t("MATCHING SIZE") },
            { title: t("FINALIZING RESULT"),     desc: t("Almost done — preparing your suggested size."),           viewfinderText: t("FINALIZING") },
          ]
      : [
        { title: t("DETECTING POSE"),     desc: t("Identifying body landmarks from your photo."),                viewfinderText: t("DETECTING POSE") },
        { title: t("SCANNING FRAME"),     desc: t("Our AI is mapping your proportions for a size suggestion."), viewfinderText: t("SCANNING FRAME") },
        { title: t("ANALYZING BODY"),     desc: t("Measuring shoulders, chest, waist and hips."),                  viewfinderText: t("ANALYZING") },
        { title: t("MATCHING SIZE"),      desc: t("Comparing your measurements to the size guide."),              viewfinderText: t("MATCHING SIZE") },
        { title: t("FINALIZING RESULT"),  desc: t("Almost done — preparing your suggested size."),                viewfinderText: t("FINALIZING") },
      ]
    : [
        { title: t("READING YOUR PROFILE"), desc: t("Loading your saved measurements and body shape answers."),    viewfinderText: t("READING PROFILE") },
        { title: t("ESTIMATING BODY"),      desc: t("Computing chest, waist, hips, sleeve and inseam from your basics."), viewfinderText: t("ESTIMATING BODY") },
        { title: t("MATCHING SIZE"),        desc: t("Comparing your measurements to the garment's size guide."),     viewfinderText: t("MATCHING SIZE") },
        { title: t("FINALIZING RESULT"),    desc: t("Almost done — preparing your suggested size."),                  viewfinderText: t("FINALIZING") },
      ];

  // Image natural dimensions for the SVG overlay
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 800, h: 1200 });
  const handleImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setDims({ w: img.naturalWidth || img.offsetWidth, h: img.naturalHeight || img.offsetHeight });
  };

  // Each stage gets its own dwell time, independent of sizing latency.
  // The user sees a clear "READING…" → "ESTIMATING…" → "MATCHING…" → …
  // progression that they can actually read, regardless of whether the
  // backend resolves in 200 ms or 6 s. The final stage is sticky — once
  // we land on "Finalizing result" we hold there until the parent view
  // unmounts. Previously the stages were squashed into the parent's
  // minVisible window (now 2 s), which made them flash by too fast on
  // mobile to read.
  const STAGE_DWELL_MS = 1800;
  const [stageIdx, setStageIdx] = useState(0);
  useEffect(() => {
    if (stageIdx >= stages.length - 1) return;
    const id = setTimeout(() => setStageIdx((i) => Math.min(stages.length - 1, i + 1)), STAGE_DWELL_MS);
    return () => clearTimeout(id);
  }, [stageIdx, stages.length]);

  // Photo mode: jump past "DETECTING POSE" the moment landmarks land —
  // no need to wait for the interval.
  useEffect(() => {
    if (isPhotoMode && landmarksReady && stageIdx === 0) {
      setStageIdx(1);
    }
  }, [isPhotoMode, landmarksReady, stageIdx]);

  const current = stages[stageIdx] ?? stages[0];

  // (LIVE_FEED badge was removed — feedTick state retired with it.)
  void sizingDone;

  return (
    <div className="ps-msc-root">
      {/* ── Photo + MediaPipe overlay only — no dark viewfinder, no scanner ── */}
      <div className="ps-msc-viewfinder">
        {displayImage && (
          <img
            src={displayImage}
            alt={t("Your photo")}
            className="ps-msc-photo"
            onLoad={handleImgLoad}
          />
        )}

        {/* MediaPipe overlay: face mesh for close-up head/eyewear, body skeleton for full-body photos. */}
        {showBodyOverlay && (
          <div className="ps-msc-pose-wrap">
            <MobileSkeleton landmarks={bodyLandmarks} w={dims.w} h={dims.h} />
          </div>
        )}
        {showFaceOverlay && (
          <div className="ps-msc-pose-wrap">
            <MobileFaceOverlay landmarks={faceLandmarks} w={dims.w} h={dims.h} />
          </div>
        )}
      </div>

      {/* ── Cycling stage title + description (slide-fade) ── */}
      <div className="ps-msc-stage">
        <div className="ps-msc-stage-slot" key={stageIdx}>
          <div className="ps-msc-stage-title">{current.title}</div>
          <div className="ps-msc-stage-desc">{current.desc}</div>
        </div>
      </div>

      <div className="ps-bpm-spacer" />

      {/* Bottom tabs were here — intentionally removed. Once analysis
          begins, switching modes mid-scan would abandon the in-flight
          Gemini call. The MANUAL FIT / AI SCAN tab control lives on the
          photo upload step, before scan starts. */}
    </div>
  );
}
